// Radish Direct's storefront operations (ops plan 2026-10-22-radish-direct §2, §3 D1; unit plan repos/direct): what the
// direct-edge BFF asks the cloud for one store, whose host name it resolved to that store's location. Every one admits
// only the `storefront` audience, and the router refuses a storefront call naming any other location
// (`storefrontCallInScope`). The cloud serves them (D2, the menu D2b).
//
// Money is untouched: every price, tax, tip and total is the server's (the quote runs `online.submitOrder`'s own
// pricing), and the menu is public, so it carries no secret and nothing private to the merchant (a test walks it).
import { z } from 'zod';
import { defineError } from '../../define/errors.ts';
import { defineQuery } from '../../define/operations.ts';
import { CategoryId, EpochMs, LocationId, Money, ProductId, Rate } from '../../primitives/codecs.ts';
import { DayHours, OnlineStoreNotFound, StoreBanner } from './index.ts';
import { OnlineItemInvalid, OnlineOrderStatus, OnlineTotals, OrderItemRequest } from './orders.ts';

const storefront = {
  availability: 'cloud',
  cloudReason: 'Radish Direct is an internet storefront: the BFF asks the cloud, which holds the store, prices orders and charges cards.',
  permission: { session: 'storefront' },
  scope: 'location',
  agent: { expose: false },
} as const;

// ---- the menu --------------------------------------------------------------------------------------------------------
const StorefrontOption = z.object({ optionId: z.string().min(1).max(64), name: z.string().max(255), price: Money }).meta({ id: 'StorefrontOption' });

/** A modifier set as the diner picks from it (the product's and its variant's overrides already applied). */
const StorefrontModifierGroup = z
  .object({
    groupId: z.string().min(1).max(64),
    /** The set's online title, else its name. */
    name: z.string().max(255),
    required: z.boolean(),
    minimum: z.int().min(0).max(999),
    /** Distinct options the diner may pick; 0 = no limit. */
    maximum: z.int().min(0).max(999),
    allowMultipleQuantity: z.boolean(),
    options: z.array(StorefrontOption).max(200),
    /** A variant's own overrides: the set as it reads once that variant is picked. Absent = as above for every variant. */
    variantIds: z.array(z.string().min(1).max(64)).max(50).optional(),
  })
  .meta({ id: 'StorefrontModifierGroup' });

const StorefrontProduct = z
  .object({
    productId: ProductId,
    /** The product's online name, else its name (legacy `online_order_name ?? name`). */
    name: z.string().max(255),
    description: z.string().max(2000).nullable(),
    imageUrl: z.url().max(2000).nullable(),
    /** A product with no variants sells at this price; one with variants shows its first variant's (legacy, Q-52). */
    price: Money,
    variants: z.array(z.object({ variantId: z.string().min(1).max(64), name: z.string().max(255), price: Money })).max(100),
    modifierGroups: z.array(StorefrontModifierGroup).max(50),
    /** False when 86'd at the location: shown, not orderable. */
    available: z.boolean(),
  })
  .meta({ id: 'StorefrontProduct' });

const StorefrontCategory = z
  .object({ categoryId: CategoryId, name: z.string().max(255), parentId: CategoryId.nullable(), productIds: z.array(ProductId).max(2000) })
  .meta({ id: 'StorefrontCategory' });

/** The store as the diner sees it: the heading, the location dialog, the hours and the pickup estimate. */
const StorefrontStore = z
  .object({
    name: z.string().max(255),
    description: z.string().max(2000),
    address: z.string().max(255),
    city: z.string().max(255),
    state: z.string().max(255),
    zipcode: z.string().max(10),
    phone: z.string().max(32),
    /** IANA zone the hours are on. */
    timezone: z.string().max(64),
    /** Menu visible, ordering off. */
    catalogMode: z.boolean(),
    menuHours: z.array(DayHours).max(7),
    /** The default prep time's minutes: "ASAP (Ready in N min)". Null when the store has none. */
    prepMinutes: z.int().min(1).max(1440).nullable(),
    /** The active banner, if any (its message and details only). */
    banner: StoreBanner.pick({ message: true, additionalDetails: true, themeColor: true, icon: true }).nullable(),
  })
  .meta({ id: 'StorefrontStore' });

export const StorefrontMenu = z
  .object({
    store: StorefrontStore,
    /** The location's sales tax rate, for the cart's "TAX (7.20%)" label. The amounts always come from the quote. */
    taxRate: Rate,
    /** In menu order, depth first (legacy MenuResponse). */
    categories: z.array(StorefrontCategory).max(500),
    products: z.array(StorefrontProduct).max(5000),
    /** Changes whenever the catalog or the store does: the BFF's cache key. */
    menuVersion: z.string().min(1).max(64),
  })
  .meta({ id: 'StorefrontMenu' });

// ---- errors ----------------------------------------------------------------------------------------------------------
/** No order of this location has that id (or it never left pending payment). */
export const StorefrontReceiptNotFound = defineError({
  code: 'online.receipt_not_found', httpStatus: 404, message: 'errors.online.receipt_not_found', retryable: false,
  details: z.object({}),
});

// ---- queries ---------------------------------------------------------------------------------------------------------
export const GetStorefrontMenu = defineQuery({
  ...storefront,
  name: 'online.storefront.menu',
  summary: "The store's public menu: the store's heading and hours, its categories and the products sold online, with their variants and modifier sets.",
  input: z.object({ locationId: LocationId }),
  output: StorefrontMenu,
  errors: [OnlineStoreNotFound],
  legacy: ['GET /v1/online-store/{id}/menu'],
});

export const GetStorefrontStatus = defineQuery({
  ...storefront,
  name: 'online.storefront.status',
  summary: 'Whether the store takes orders now, until when, or when it opens next (the gate online.submitOrder applies).',
  input: z.object({ locationId: LocationId }),
  output: z.object({ status: z.enum(['online', 'offline']), openUntil: EpochMs.nullable(), opensNext: EpochMs.nullable(), catalogMode: z.boolean() }),
  errors: [OnlineStoreNotFound],
  legacy: ['GET /v1/online-store/{id}/status'],
});

/**
 * The cart priced by the cloud, exactly as `online.submitOrder` would price it now: Pay sends the quote's total as the
 * order's `amount`. Legacy's order verification (the price-changed notice) compares the cart's lines with these.
 */
export const QuoteStorefrontOrder = defineQuery({
  ...storefront,
  name: 'online.storefront.quote',
  summary: "Price the diner's cart as the order would be priced now: each line, subtotal, tax, tip and total.",
  input: z.object({ locationId: LocationId, items: z.array(OrderItemRequest).min(1).max(200), tip: Money }),
  output: z.object({
    totals: OnlineTotals,
    /**
     * Checkout's "Add a Tip (Optional)" tiles: each rate of the pre-tax subtotal with the tip it adds, as legacy's
     * browser computed it (core `storefrontTipSuggestions`). Picking a tile sends its `tip` as the order's tip.
     */
    tipSuggestions: z.array(z.object({ rate: Rate, tip: Money })).max(8),
  }),
  errors: [OnlineStoreNotFound, OnlineItemInvalid],
  legacy: ['POST /v1/online-store/{id}/order-verification'],
});

/**
 * Only what the browser's hosted card fields need. A gateway the cloud would not charge (Payrix, or one whose keys or
 * reach this cloud lacks) reads as none. `mode`: which of the gateway's hosts serves its fields (null for the sandbox).
 */
export const GetStorefrontPaymentConfig = defineQuery({
  ...storefront,
  name: 'online.storefront.paymentConfig',
  summary: "The store's card gateway and its public tokenization key, for the checkout's hosted card fields.",
  input: z.object({ locationId: LocationId }),
  output: z.object({
    gateway: z.enum(['sandbox', 'datacap', 'clearent']).nullable(),
    publicKey: z.string().max(2000).nullable(),
    mode: z.enum(['test', 'live']).nullable(),
  }),
  errors: [OnlineStoreNotFound],
  legacy: ['GET /v1/online-store/{id}/menu (its payment fields)'],
});

/**
 * The confirmation page, reload-safe (FIX Q-44). The BFF asks for it only after checking the diner's receipt token, which
 * it derived itself (a keyed hash of the order) when the order was placed and checks on every read; the token never
 * reaches the cloud, so no command record or replay store can hold it (review of #1912).
 */
export const GetStorefrontOrderReceipt = defineQuery({
  ...storefront,
  name: 'online.storefront.orderReceipt',
  summary: "The diner's order confirmation, for the BFF once it has checked the diner's receipt token.",
  input: z.object({ locationId: LocationId, onlineOrderId: z.uuid({ version: 'v7' }) }),
  output: z.object({
    number: z.string(),
    status: OnlineOrderStatus,
    orderName: z.string().nullable(),
    tableId: z.string().nullable(),
    totals: OnlineTotals,
    placedAt: EpochMs,
    estimateReadyAt: EpochMs.nullable(),
  }),
  errors: [StorefrontReceiptNotFound],
});

/** No store has that subdomain (or its store is closed for good). The BFF answers its own 404 page. */
export const StorefrontNotFound = defineError({
  code: 'online.storefront_not_found', httpStatus: 404, message: 'errors.online.storefront_not_found', retryable: false,
  details: z.object({}),
});

/** A store's subdomain: one DNS label, lowercase (the BFF reads it from the host, edge.ts `subdomainOf`). */
export const StorefrontSubdomain = z.string().regex(/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/);

/**
 * Host to store, the BFF's first question about every host before it knows any store (review of #1917: its `main.ts`
 * asked this before anything declared it). Platform-scoped: the call names no location, and the router admits a
 * storefront principal's call to it only when the call names none (`storefrontCallInScope`). It answers only the
 * location id, which the host already makes public; the BFF keeps answers in a bounded cache and limits each address's
 * uncached lookups. Served with D2; the api admits the storefront credential with D3b.
 */
export const GetStorefrontDirectory = defineQuery({
  ...storefront,
  scope: 'platform',
  name: 'online.storefront.directory',
  summary: 'Which store a storefront subdomain belongs to: the BFF resolving a host to its location.',
  input: z.object({ subdomain: StorefrontSubdomain }),
  output: z.object({ locationId: LocationId }),
  errors: [StorefrontNotFound],
});

/** Every storefront query, served by the cloud (server modules/online/internal/storefront.ts). */
export const onlineStorefrontQueries = [
  GetStorefrontDirectory, GetStorefrontMenu, GetStorefrontStatus, QuoteStorefrontOrder, GetStorefrontPaymentConfig, GetStorefrontOrderReceipt,
] as const;
